/**
 * Individual Property Request — Unified Submission Wizard
 *
 * One flow that merges the former "أضف عقارك" (add property) and "تفويض عقار"
 * (authorize property) entries. It takes the multi-step onboarding UX of the old
 * authorization wizard and wraps it around the REAL `PropertyForm` and the WORKING
 * publish pipeline (`POST /registration/individual-requests` → DRAFT Property +
 * pending_review IndividualPropertyRequest → admin approve → live + email).
 *
 * Steps: Consent → Authorization type → Nafath identity (soft gate) → Ownership /
 * deed → Declarations → E-signature → Contact preferences → Property details. The
 * final step renders <PropertyForm>; its own submit button drives handleSubmit,
 * which merges the wizard's accumulated extras, uploads images + the deed document,
 * and POSTs everything in one atomic call.
 *
 * Entered after accepting the FAL disclaimer at /fal-disclaimer.
 *
 * ── Security note ──────────────────────────────────────────────────────────
 * The PUBLIC contact on a FAL-managed individual listing is the PLATFORM agent
 * (set backend-side via agentId), NOT the owner. The owner's name/phone/email
 * land ONLY on the IndividualPropertyRequest row (ownerName/ownerPhone/ownerEmail)
 * — never on the public Property. The unified form's "contact" fields
 * (agentName/agentPhone/agentEmail) are, for an individual, the OWNER's contact,
 * so handleSubmit STRIPS them from the property payload and re-sends them as
 * owner* fields. The `showPhone` preference is request metadata only — it must
 * NOT surface on the public Property.
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import Stepper from '../../components/base/Stepper';
import Badge from '../../components/base/Badge';
import Input from '../../components/base/Input';
import Button from '../../components/base/Button';
import PropertyForm, { type PropertyFormSubmit } from '../../components/property/PropertyForm';
import NafathVerification, {
  mapApiStatusToNafathStatus,
  type NafathStatus,
} from '../../components/verification/NafathVerification';
import { useAuth } from '../../hooks/useAuth';
import { submitIndividualListing } from '../../services/registrationService';
import {
  initiateNafath,
  checkNafathStatus,
  fetchNafathMe,
  type NafathMeResponse,
} from '../../services/nafathService';
import { ApiError, resolveApiBaseUrl } from '../../utils/apiClient';
import {
  type FormState,
  INITIAL_FORM,
  parseList,
  formStateToPayload,
  mapServerErrorToFields,
} from '../../utils/propertyFormState';
import { uploadNewImages } from '../../utils/uploadPropertyImages';

const API_BASE_URL = resolveApiBaseUrl();

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000; // Match backend TTL

const AUTH_TYPES = ['adOnly', 'adMarketing', 'fullService'] as const;
type AuthType = (typeof AUTH_TYPES)[number] | '';
type InquiryHandler = 'platform' | 'owner' | 'both';
type PreferredTime = 'anytime' | 'morning' | 'afternoon' | 'evening';

// Wizard step indices (the final step is the real PropertyForm).
const STEP = {
  consent: 0,
  authType: 1,
  identity: 2,
  ownership: 3,
  declarations: 4,
  signature: 5,
  contact: 6,
  property: 7,
} as const;
const LAST_STEP = STEP.property;

// State for the NON-property steps. PropertyForm owns the property/image state and
// surfaces it only through its onSubmit callback — so it needs no refactor.
interface WizardExtras {
  acceptedTerms: boolean;
  authType: AuthType;
  deedType: 'deed' | 'contract' | 'powerOfAttorney' | '';
  deedNumber: string;
  deedDate: string;
  ownerIdNumber: string;
  deedFile: File | null;
  declarations: Record<string, boolean>;
  signatureData: string;
  inquiryHandler: InquiryHandler;
  showPhone: boolean;
  preferredTime: PreferredTime;
}

const INITIAL_EXTRAS: WizardExtras = {
  acceptedTerms: false,
  authType: '',
  deedType: '',
  deedNumber: '',
  deedDate: '',
  ownerIdNumber: '',
  deedFile: null,
  declarations: {},
  signatureData: '',
  inquiryHandler: 'platform',
  showPhone: false,
  preferredTime: 'anytime',
};

// Image upload (property photos) uses the shared helper — see the import above.
// Documents have their own endpoint, kept below.

// Upload a single document (deed/authorization) — PDF or image — to the documents
// endpoint. Returns the uploaded URL or null on failure.
const uploadDocumentToServer = async (file: File, token: string | undefined): Promise<string | null> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_BASE_URL}/images/upload/document`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!response.ok) {
      console.error('Document upload failed:', response.status, response.statusText);
      return null;
    }
    const data = await response.json();
    return data.secure_url || data.url || null;
  } catch (error) {
    console.error('Document upload error:', error);
    return null;
  }
};

// ---------------------------------------------------------------------------
// Inline signature pad — captures a drawn signature as a PNG data URL.
// ---------------------------------------------------------------------------
function SignaturePad({ onChange }: { onChange: (dataUrl: string) => void }) {
  const { t } = useTranslation('authorization');
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastRef = useRef<{ x: number; y: number } | null>(null);

  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = pos(e);
    canvasRef.current?.setPointerCapture(e.pointerId);
  };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx || !lastRef.current) return;
    const p = pos(e);
    ctx.strokeStyle = '#0F1A18';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
  };
  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastRef.current = null;
    const canvas = canvasRef.current;
    if (canvas) onChange(canvas.toDataURL('image/png'));
  };
  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (ctx && canvas) ctx.clearRect(0, 0, canvas.width, canvas.height);
    onChange('');
  };

  return (
    <div>
      <div className="rounded-xl border-2 border-dashed border-[var(--pv-border-soft)] bg-white">
        <canvas
          ref={canvasRef}
          width={600}
          height={200}
          className="w-full h-[200px] touch-none rounded-xl cursor-crosshair"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
        />
      </div>
      <div className="mt-2 flex items-center justify-between">
        <p className="text-xs text-[var(--pv-color-text-muted)]">{t('signature.canvasHint')}</p>
        <button type="button" onClick={clear} className="text-sm text-[var(--pv-color-primary)] hover:underline">
          <i className="ri-eraser-line mr-1"></i>
          {t('signature.clear')}
        </button>
      </div>
    </div>
  );
}

export default function IndividualPropertyRequestPage() {
  const { t, i18n } = useTranslation(['authorization', 'registration', 'common']);
  const isRTL = i18n.dir() === 'rtl';
  const locale: 'ar' | 'en' = isRTL ? 'ar' : 'en';
  const navigate = useNavigate();
  const { user, session } = useAuth();

  const [currentStep, setCurrentStep] = useState(0);
  const [extras, setExtras] = useState<WizardExtras>(INITIAL_EXTRAS);

  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [serverFieldErrors, setServerFieldErrors] = useState<Record<string, string>>({});
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);

  // --- Nafath (soft gate) state ---
  const [me, setMe] = useState<NafathMeResponse | null>(null);
  const [nationalId, setNationalId] = useState('');
  const [nafathStatus, setNafathStatus] = useState<NafathStatus>('not_started');
  const [verificationCode, setVerificationCode] = useState<string | undefined>();
  const [nafathError, setNafathError] = useState<string | null>(null);
  const [nafathSubmitting, setNafathSubmitting] = useState(false);
  const transIdRef = useRef<string | undefined>(undefined);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollStartedAtRef = useRef<number>(0);

  const nafathAvailable = me?.available ?? false;
  const nafathCompleted = nafathStatus === 'completed';

  const updateExtras = (patch: Partial<WizardExtras>) => setExtras((prev) => ({ ...prev, ...patch }));

  // FAL-disclaimer gate: the owner must accept the FAL disclaimer (at
  // /fal-disclaimer) before reaching this page. Redirect back if they haven't.
  useEffect(() => {
    const falAccepted = localStorage.getItem('safaqat24.fal.accepted');
    if (falAccepted !== 'true') {
      navigate('/fal-disclaimer');
    }
  }, [navigate]);

  // Read Nafath availability once (reflects whether NAFATH_APP_ID is set).
  useEffect(() => {
    if (!session?.token) return;
    let cancelled = false;
    fetchNafathMe(session.token)
      .then((data) => {
        if (!cancelled) setMe(data);
      })
      .catch(() => {
        // Treat a failed /me as "unavailable" → soft gate proceeds.
        if (!cancelled) setMe({ available: false } as NafathMeResponse);
      });
    return () => {
      cancelled = true;
    };
  }, [session?.token]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);
  useEffect(() => stopPolling, [stopPolling]);

  const poll = useCallback(
    async (currentTransId: string) => {
      if (!session?.token) return;
      try {
        const result = await checkNafathStatus(currentTransId, session.token);
        setNafathStatus(mapApiStatusToNafathStatus(result.status));
        if (result.status !== 'WAITING') {
          stopPolling();
          return;
        }
        if (Date.now() - pollStartedAtRef.current >= MAX_POLL_DURATION_MS) {
          setNafathStatus('expired');
          stopPolling();
          return;
        }
        pollTimerRef.current = setTimeout(() => poll(currentTransId), POLL_INTERVAL_MS);
      } catch {
        if (Date.now() - pollStartedAtRef.current >= MAX_POLL_DURATION_MS) {
          setNafathStatus('expired');
          stopPolling();
          return;
        }
        pollTimerRef.current = setTimeout(() => poll(currentTransId), POLL_INTERVAL_MS);
      }
    },
    [session?.token, stopPolling],
  );

  const handleNafathInitiate = async () => {
    if (!session?.token) return;
    setNafathError(null);
    if (!/^\d{10}$/.test(nationalId)) {
      setNafathError(isRTL ? 'يجب أن يتكون رقم الهوية من 10 أرقام' : 'National ID must be exactly 10 digits');
      return;
    }
    setNafathSubmitting(true);
    try {
      const result = await initiateNafath(nationalId, locale, session.token);
      transIdRef.current = result.transId;
      setVerificationCode(result.random);
      setNafathStatus('waiting');
      pollStartedAtRef.current = Date.now();
      pollTimerRef.current = setTimeout(() => poll(result.transId), POLL_INTERVAL_MS);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setNafathError(isRTL ? 'محاولات كثيرة. انتظر قليلاً وحاول مجدداً.' : 'Too many attempts. Please wait and retry.');
      } else if (err instanceof ApiError) {
        setNafathError(err.message);
      } else {
        setNafathError(isRTL ? 'حدث خطأ. حاول مرة أخرى.' : 'Something went wrong. Please try again.');
      }
    } finally {
      setNafathSubmitting(false);
    }
  };

  const handleNafathCancel = () => {
    stopPolling();
    setNafathStatus('not_started');
    setVerificationCode(undefined);
    transIdRef.current = undefined;
  };
  const handleNafathRetry = () => {
    setNafathStatus('not_started');
    setVerificationCode(undefined);
    transIdRef.current = undefined;
    setNafathError(null);
  };

  // Pre-fill the contact section (which, for an individual, is the OWNER's
  // contact) from the authenticated user. These flow into the form's
  // agentName/agentPhone/agentEmail and are later re-mapped to owner* fields.
  const initialForm = useMemo<FormState>(() => {
    if (!user) return { ...INITIAL_FORM };
    const fullName = user.fullName?.trim() || `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return {
      ...INITIAL_FORM,
      agentName: fullName,
      agentPhone: user.phoneNumber || '',
      agentEmail: user.email || '',
    };
  }, [user]);

  const steps = useMemo(
    () => [
      { id: 'consent', label: t('authorization:steps.consent'), icon: 'ri-shield-check-line' },
      { id: 'authType', label: t('authorization:steps.authType'), icon: 'ri-file-list-3-line' },
      { id: 'identity', label: t('authorization:steps.identity'), icon: 'ri-fingerprint-line' },
      { id: 'ownership', label: t('authorization:steps.ownership'), icon: 'ri-government-line' },
      { id: 'declarations', label: t('authorization:steps.declarations'), icon: 'ri-checkbox-multiple-line' },
      { id: 'signature', label: t('authorization:steps.signature'), icon: 'ri-pen-nib-line' },
      { id: 'contact', label: t('authorization:steps.contact'), icon: 'ri-customer-service-2-line' },
      { id: 'details', label: t('authorization:steps.details'), icon: 'ri-home-add-line' },
    ],
    [t],
  );

  const declarationItems = useMemo(
    () =>
      (t('authorization:declarations.items', { returnObjects: true }) as Array<{
        id: string;
        title: string;
        text: string;
      }>) || [],
    [t],
  );

  const canProceed = (): boolean => {
    switch (currentStep) {
      case STEP.consent:
        return extras.acceptedTerms;
      case STEP.authType:
        return extras.authType !== '';
      case STEP.identity:
        // Soft gate: only block when Nafath is actually live.
        return !nafathAvailable || nafathCompleted;
      case STEP.ownership:
        return Boolean(extras.deedType && extras.deedNumber.trim());
      case STEP.declarations:
        return declarationItems.length > 0 && declarationItems.every((it) => extras.declarations[it.id]);
      case STEP.signature:
        return extras.signatureData !== '';
      case STEP.contact:
        return true; // inquiryHandler defaults to 'platform'
      default:
        return true;
    }
  };

  const handleNext = () => setCurrentStep((s) => Math.min(s + 1, LAST_STEP));
  const handlePrevious = () => setCurrentStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async (data: PropertyFormSubmit) => {
    if (submitting) return;
    const { form, imageFiles, coverImageIndex, floorPlanIndices } = data;

    if (!session?.token) {
      setSubmissionError(t('registration:individualRequest.submitError'));
      return;
    }

    setSubmitting(true);
    setSubmissionError(null);
    setServerFieldErrors({});
    setUploadProgress(null);

    try {
      // 1. Upload NEW images via the shared helper (returns null if any fail).
      const uploaded = await uploadNewImages(imageFiles, session.token, {
        floorPlanIndices,
        onProgress: (current, total) =>
          setUploadProgress(
            isRTL ? `جارٍ رفع الصور... (${current}/${total})` : `Uploading images... (${current}/${total})`,
          ),
      });
      if (!uploaded) {
        const msg = isRTL
          ? 'تعذّر رفع بعض الصور. يرجى إزالتها والمحاولة مرة أخرى.'
          : 'Some images failed to upload. Please remove them and try again.';
        setServerFieldErrors({ _form: msg });
        setSubmissionError(msg);
        setUploadProgress(null);
        setSubmitting(false);
        return;
      }
      const uploadedImageUrls = uploaded.uploadedUrls;
      const uploadedFloorPlanUrls = uploaded.floorPlanUrls;

      // 2. Upload the deed document, if one was attached.
      let deedUrl: string | undefined;
      if (extras.deedFile) {
        setUploadProgress(isRTL ? 'جارٍ رفع مستند الملكية...' : 'Uploading ownership document...');
        const url = await uploadDocumentToServer(extras.deedFile, session.token);
        if (!url) {
          const msg = t('authorization:ownership.uploadError');
          setServerFieldErrors({ _form: msg });
          setSubmissionError(msg);
          setUploadProgress(null);
          setSubmitting(false);
          return;
        }
        deedUrl = url;
      }

      setUploadProgress(isRTL ? 'جارٍ إرسال الطلب...' : 'Submitting your request...');

      // Combine uploaded URLs with any manually entered gallery URLs, then move
      // the cover image to the front — identical to the create page.
      const manualGalleryUrls = parseList(form.galleryUrls).filter((url) => url && !url.startsWith('data:'));
      const allImageUrls = [...uploadedImageUrls, ...manualGalleryUrls];
      const safeCoverIndex = Math.min(coverImageIndex, allImageUrls.length - 1);
      const finalImageUrls =
        allImageUrls.length > 0 && safeCoverIndex >= 0
          ? [allImageUrls[safeCoverIndex], ...allImageUrls.filter((_, i) => i !== safeCoverIndex)]
          : allImageUrls;

      // 3. Build the payload. Start from the shared mapper, then STRIP every
      //    owner-PII / not-applicable field from the PROPERTY body and re-send
      //    the owner's contact + the wizard's verification extras.
      const base = formStateToPayload(form, finalImageUrls, uploadedFloorPlanUrls);
      delete base.agentName;
      delete base.agentPhone;
      delete base.agentEmail;
      delete base.agentWhatsapp; // owner PII must NOT go on the public Property
      delete base.listUnder;

      const payload = {
        ...base,
        ownerName: form.agentName,
        ownerPhone: form.agentPhone,
        ownerEmail: form.agentEmail || undefined,
        ownerIdNumber: extras.ownerIdNumber || undefined,
        authorizationConsent: true,
        agreedToTerms: true,
        commissionRate: 250, // 2.5%
        // Verification / authorization extras from the wizard.
        authorizationType: extras.authType || undefined,
        deedType: extras.deedType || undefined,
        deedNumber: extras.deedNumber || undefined,
        deedDate: extras.deedDate || undefined,
        authorizationDocumentUrl: deedUrl,
        declarations: extras.declarations,
        signatureData: extras.signatureData || undefined,
        inquiryHandler: extras.inquiryHandler,
        showPhone: extras.showPhone,
        preferredContactTime: extras.preferredTime,
        // Nafath snapshot (soft gate — only meaningful when the integration is live).
        nafathVerified: nafathAvailable && nafathCompleted,
        nafathTransId: nafathCompleted ? transIdRef.current : undefined,
        verifiedNationalId: nafathCompleted ? nationalId : undefined,
      };

      // 4. Single atomic POST → DRAFT Property + pending_review request.
      await submitIndividualListing(payload, session.token);

      navigate('/my-property-requests', {
        state: { successMessage: t('registration:individualRequest.submitSuccess') },
      });
    } catch (error) {
      console.error('Failed to submit property request:', error);
      setUploadProgress(null);
      if (error instanceof ApiError) {
        const errorBody = error.details as
          | { message?: string; details?: Array<{ path?: (string | number)[]; message: string }> }
          | undefined;
        const fieldErrors = mapServerErrorToFields(errorBody ?? { message: error.message });
        setServerFieldErrors(fieldErrors);
        setSubmissionError(
          fieldErrors._form ?? errorBody?.message ?? error.message ?? t('registration:individualRequest.submitError'),
        );
      } else if (error instanceof Error) {
        setSubmissionError(error.message);
      } else {
        setSubmissionError(t('registration:individualRequest.submitError'));
      }
      setSubmitting(false);
    }
  };

  // Don't render until we know the user is authenticated.
  if (!user) return null;

  const inputClass =
    'w-full px-4 py-3 bg-[var(--pv-color-surface-soft)] border border-[var(--pv-border-soft)] rounded-xl text-[var(--pv-color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--pv-color-primary)]/20';
  const labelClass = 'block text-sm font-medium text-[var(--pv-color-text)] mb-2';

  const renderStep = () => {
    switch (currentStep) {
      case STEP.consent:
        return (
          <div className="space-y-6">
            <div className="p-4 bg-[var(--pv-color-surface-soft)] rounded-xl">
              <h4 className="font-medium text-[var(--pv-color-text)] mb-3">{t('authorization:consent.termsTitle')}</h4>
              <ul className="space-y-2 text-sm text-[var(--pv-color-text-muted)]">
                {(t('authorization:consent.terms', { returnObjects: true }) as string[]).map((term, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <i className="ri-check-line text-green-500 mt-0.5"></i>
                    {term}
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
              <p className="text-sm text-[var(--pv-color-text)]">{t('authorization:consent.agreement')}</p>
            </div>
            <label className="flex items-center gap-3 p-4 border-2 border-[var(--pv-border-soft)] rounded-xl cursor-pointer hover:border-[var(--pv-color-primary)]/50 transition-colors">
              <input
                type="checkbox"
                checked={extras.acceptedTerms}
                onChange={(e) => updateExtras({ acceptedTerms: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 text-[var(--pv-color-primary)] focus:ring-[var(--pv-color-primary)]"
              />
              <span className="font-medium text-[var(--pv-color-text)]">{t('authorization:consent.acceptTerms')}</span>
            </label>
          </div>
        );

      case STEP.authType:
        return (
          <div className="space-y-4">
            {AUTH_TYPES.map((type, index) => (
              <button
                key={type}
                type="button"
                onClick={() => updateExtras({ authType: type })}
                className={`w-full p-5 rounded-xl border-2 text-start transition-all ${
                  extras.authType === type
                    ? 'border-[var(--pv-color-primary)] bg-[var(--pv-color-primary)]/10'
                    : 'border-[var(--pv-border-soft)] hover:border-[var(--pv-color-primary)]/50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-[var(--pv-color-text)]">{t(`authorization:authType.types.${type}.title`)}</h4>
                      {index === 1 && (
                        <Badge variant="primary" size="sm">
                          {t('authorization:authType.recommended')}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-[var(--pv-color-text-muted)] mt-1">{t(`authorization:authType.types.${type}.description`)}</p>
                    <ul className="mt-3 space-y-1">
                      {(t(`authorization:authType.types.${type}.features`, { returnObjects: true }) as string[]).map((feature, i) => (
                        <li key={i} className="text-sm text-[var(--pv-color-text-muted)] flex items-center gap-2">
                          <i className="ri-check-line text-green-500"></i>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      extras.authType === type ? 'border-[var(--pv-color-primary)] bg-[var(--pv-color-primary)]' : 'border-gray-300'
                    }`}
                  >
                    {extras.authType === type && <i className="ri-check-line text-white text-sm"></i>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        );

      case STEP.identity:
        if (!nafathAvailable) {
          // Soft gate: NAFATH_APP_ID not set → "coming soon", submission proceeds.
          return (
            <div className="space-y-4">
              <NafathVerification status="not_started" isApiReady={false} />
              <p className="text-center text-sm text-[var(--pv-color-text-muted)]">{t('authorization:identity.softGateNote')}</p>
            </div>
          );
        }
        if (nafathStatus === 'not_started') {
          return (
            <div className="space-y-4">
              <p className="text-sm text-[var(--pv-color-text)]">{t('authorization:identity.subtitle')}</p>
              <div>
                <label htmlFor="nationalId" className={labelClass}>
                  {t('authorization:identity.nationalIdLabel')}
                </label>
                <Input
                  id="nationalId"
                  type="text"
                  inputMode="numeric"
                  maxLength={10}
                  value={nationalId}
                  onChange={(e) => setNationalId(e.target.value.replace(/\D/g, ''))}
                  placeholder={t('authorization:identity.nationalIdPlaceholder')}
                  disabled={nafathSubmitting}
                />
              </div>
              {nafathError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{nafathError}</p>
                </div>
              )}
              <Button variant="primary" onClick={handleNafathInitiate} disabled={nafathSubmitting || nationalId.length !== 10}>
                <i className={`ri-fingerprint-line ${isRTL ? 'ml-2' : 'mr-2'}`}></i>
                {nafathSubmitting ? t('authorization:identity.verifying') : t('authorization:identity.verifyCta')}
              </Button>
            </div>
          );
        }
        return (
          <NafathVerification
            status={nafathStatus}
            verificationCode={verificationCode}
            isApiReady
            onCancel={handleNafathCancel}
            onRetry={handleNafathRetry}
          />
        );

      case STEP.ownership:
        return (
          <div className="space-y-6">
            <div>
              <label className={labelClass}>{t('authorization:ownership.deedType')}</label>
              <select value={extras.deedType} onChange={(e) => updateExtras({ deedType: e.target.value as WizardExtras['deedType'] })} className={inputClass}>
                <option value="">{isRTL ? 'اختر' : 'Select'}</option>
                <option value="deed">{t('authorization:ownership.deedTypes.deed')}</option>
                <option value="contract">{t('authorization:ownership.deedTypes.contract')}</option>
                <option value="powerOfAttorney">{t('authorization:ownership.deedTypes.powerOfAttorney')}</option>
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>{t('authorization:ownership.deedNumber')}</label>
                <input
                  type="text"
                  value={extras.deedNumber}
                  onChange={(e) => updateExtras({ deedNumber: e.target.value })}
                  placeholder={t('authorization:ownership.deedNumberPlaceholder')}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>{t('authorization:ownership.deedDate')}</label>
                <input type="date" value={extras.deedDate} onChange={(e) => updateExtras({ deedDate: e.target.value })} className={inputClass} />
              </div>
            </div>
            <div>
              <label className={labelClass}>{t('authorization:ownership.ownerIdNumber')}</label>
              <input
                type="text"
                inputMode="numeric"
                value={extras.ownerIdNumber}
                onChange={(e) => updateExtras({ ownerIdNumber: e.target.value.replace(/\D/g, '') })}
                placeholder={t('authorization:ownership.ownerIdNumberPlaceholder')}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>{t('authorization:ownership.uploadDeed')}</label>
              <label className="block border-2 border-dashed border-[var(--pv-border-soft)] rounded-xl p-6 text-center hover:border-[var(--pv-color-primary)]/50 transition-colors cursor-pointer">
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  className="hidden"
                  onChange={(e) => updateExtras({ deedFile: e.target.files?.[0] ?? null })}
                />
                <i className="ri-upload-cloud-line text-4xl text-[var(--pv-color-text-muted)] mb-2"></i>
                {extras.deedFile ? (
                  <p className="text-sm font-medium text-[var(--pv-color-text)] break-all">
                    <i className="ri-file-line mr-1"></i>
                    {extras.deedFile.name}
                  </p>
                ) : (
                  <>
                    <p className="text-[var(--pv-color-text-muted)]">
                      {t('authorization:ownership.dragDrop')} <span className="text-[var(--pv-color-primary)]">{t('authorization:ownership.browse')}</span>
                    </p>
                    <p className="text-xs text-[var(--pv-color-text-muted)] mt-1">{t('authorization:ownership.acceptedFormats')}</p>
                  </>
                )}
              </label>
              <p className="text-xs text-[var(--pv-color-text-muted)] mt-1">{t('authorization:ownership.uploadDeedDescription')}</p>
            </div>
          </div>
        );

      case STEP.declarations:
        return (
          <div className="space-y-4">
            <p className="text-[var(--pv-color-text-muted)]">{t('authorization:declarations.subtitle')}</p>
            {declarationItems.map((item) => (
              <label
                key={item.id}
                className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  extras.declarations[item.id]
                    ? 'border-[var(--pv-color-primary)] bg-[var(--pv-color-primary)]/5'
                    : 'border-[var(--pv-border-soft)] hover:border-[var(--pv-color-primary)]/50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={extras.declarations[item.id] || false}
                  onChange={(e) => updateExtras({ declarations: { ...extras.declarations, [item.id]: e.target.checked } })}
                  className="w-5 h-5 mt-0.5 rounded border-gray-300 text-[var(--pv-color-primary)] focus:ring-[var(--pv-color-primary)]"
                />
                <div>
                  <p className="font-medium text-[var(--pv-color-text)]">{item.title}</p>
                  <p className="text-sm text-[var(--pv-color-text-muted)]">{item.text}</p>
                </div>
              </label>
            ))}
            <button
              type="button"
              onClick={() => {
                const all: Record<string, boolean> = {};
                declarationItems.forEach((item) => {
                  all[item.id] = true;
                });
                updateExtras({ declarations: all });
              }}
              className="w-full py-2 text-sm text-[var(--pv-color-primary)] hover:underline"
            >
              {t('authorization:declarations.acceptAll')}
            </button>
          </div>
        );

      case STEP.signature:
        return (
          <div className="space-y-4">
            <p className="text-[var(--pv-color-text-muted)]">{t('authorization:signature.manualSignatureDesc')}</p>
            <SignaturePad onChange={(dataUrl) => updateExtras({ signatureData: dataUrl })} />
            <p className="text-sm text-[var(--pv-color-text)] p-4 bg-[var(--pv-color-surface-soft)] rounded-xl">{t('authorization:signature.agreementText')}</p>
          </div>
        );

      case STEP.contact:
        return (
          // This listing publishes under Safaqat24's own FAL license, so the
          // platform is the sole point of contact — the owner doesn't choose who
          // handles inquiries. We just tell them how it works instead.
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-5 rounded-xl border border-[#1B7566]/30 bg-[var(--pv-color-surface-sunken)] dark:to-blue-900/20">
              <i className="ri-customer-service-2-line text-2xl text-[#004D40] mt-0.5"></i>
              <div>
                <p className="font-semibold text-[var(--pv-color-text)] mb-1">{t('authorization:contact.falManaged.title')}</p>
                <p className="text-sm text-[var(--pv-color-text-muted)]">{t('authorization:contact.falManaged.body')}</p>
              </div>
            </div>
          </div>
        );

      case STEP.property:
        return (
          <div className="space-y-6">
            {/* How it works / 2.5% commission info box */}
            <div className="p-5 bg-[var(--pv-color-surface-sunken)] dark:to-blue-900/20 rounded-xl border border-[#1B7566]/30 dark:border-[#1B7566]/20">
              <h3 className="flex items-center gap-2 font-medium text-gray-900 dark:text-white mb-3">
                <i className="ri-lightbulb-line text-[#004D40]"></i>
                {t('registration:individual.howItWorks')}
              </h3>
              <ol className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                {(['step1', 'step2', 'step3', 'step4'] as const).map((step, index) => (
                  <li key={step} className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20 text-[#004D40] text-xs font-medium">
                      {index + 1}
                    </span>
                    {t(`registration:individual.${step}`)}
                  </li>
                ))}
              </ol>
              <p className="text-xs text-[#004D40] dark:text-[#1B7566] mt-3 font-medium">{t('registration:individual.commission', { rate: 2.5 })}</p>
            </div>

            {submissionError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-900">
                <div className="flex items-center gap-2">
                  <i className="ri-error-warning-fill text-xl text-red-600"></i>
                  <span className="whitespace-pre-wrap">{submissionError}</span>
                </div>
              </div>
            )}

            <PropertyForm
              variant="individual"
              initialForm={initialForm}
              showListUnder={false}
              submitting={submitting}
              serverErrors={Object.keys(serverFieldErrors).length ? serverFieldErrors : undefined}
              submitLabel={isRTL ? 'إرسال للمراجعة' : 'Submit for Review'}
              uploadProgress={uploadProgress}
              onSubmit={handleSubmit}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <Navigation />

      <main className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8" dir={isRTL ? 'rtl' : 'ltr'}>
        <header className="mb-6">
          <p className="inline-flex items-center rounded-full bg-[var(--pv-color-surface-sunken)] px-3 py-1 text-sm font-semibold text-[#003930]">
            <i className="ri-home-add-line mr-2"></i>
            {t('registration:individual.title')}
          </p>
          <h1 className="mt-4 text-3xl font-bold text-[var(--pv-color-primary)] dark:text-white">{t('registration:individualRequest.pageTitle')}</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-300">{t('registration:individualRequest.pageSubtitle')}</p>
        </header>

        {/* Stepper */}
        <div className="mb-8 overflow-x-auto">
          <Stepper steps={steps} currentStep={currentStep} size="sm" />
        </div>

        <Card variant="bordered" padding="lg">
          {renderStep()}

          {/* Wizard navigation — hidden on the final step (PropertyForm owns submit). */}
          {currentStep < LAST_STEP ? (
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-[var(--pv-border-soft)]">
              <button
                type="button"
                onClick={handlePrevious}
                disabled={currentStep === 0}
                className={`px-6 py-3 rounded-xl font-medium transition-all ${
                  currentStep === 0
                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                    : 'bg-[var(--pv-color-surface-soft)] text-[var(--pv-color-text)] hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <i className={`ri-arrow-${isRTL ? 'right' : 'left'}-line ${isRTL ? 'ml-2' : 'mr-2'}`}></i>
                {t('authorization:actions.back')}
              </button>
              <button
                type="button"
                onClick={handleNext}
                disabled={!canProceed()}
                className="px-6 py-3 bg-[var(--pv-color-primary)] text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('authorization:actions.next')}
                <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-line ${isRTL ? 'mr-2' : 'ml-2'}`}></i>
              </button>
            </div>
          ) : (
            <div className="mt-6">
              <button type="button" onClick={handlePrevious} className="text-sm font-medium text-[var(--pv-color-text-muted)] hover:text-[var(--pv-color-text)] transition-colors">
                <i className={`ri-arrow-${isRTL ? 'right' : 'left'}-line ${isRTL ? 'ml-1' : 'mr-1'}`}></i>
                {t('authorization:actions.back')}
              </button>
            </div>
          )}
        </Card>
      </main>

      <Footer />
    </div>
  );
}
